import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { AlertTriangle, ArrowRight, ChevronDown, ChevronLeft, ChevronUp, Edit2, ExternalLink, Gift, Image, MapPin, MessageCircle, Phone, Plus, RotateCcw, Save, Send, Star, Trash2, X } from "lucide-react";
import TripConfirmationCard from "./TripConfirmationCard";
import { parseChatMessageLinks } from "./chatLinkUtils";
import { getDisplayName, getRoleMeta, normalizeSenderRole } from "./tripChatRoomUtils";
import shonanPalmLogo from "@/assets/shonan-palm-gold-transparent.png";

const defaultUiCopy = {
  locale: "en-US",
  title: "Trip Communication Center",
  preferredLanguage: "Display language",
  languageOptions: [
    { value: "en", label: "English" },
    { value: "ja", label: "日本語" },
    { value: "zh", label: "简体中文" },
    { value: "zh_tw", label: "繁體中文" },
  ],
  orderSummary: "Order Summary",
  service: "Service",
  serviceTypes: { transfer: "Transfer", charter: "Charter" },
  hours: "hours",
  order: "Order",
  flight: "Flight",
  vehicle: "Vehicle",
  passengers: "Passengers",
  luggage: "Luggage",
  pickup: "Pick-Up",
  dropoff: "Drop-Off",
  passengerLastViewed: "Passenger last viewed",
  driverLastViewed: "Driver last viewed",
  adminLastViewed: "Admin last viewed",
  notViewed: "Not viewed yet",
  system: "System",
  transferConfirmation: "Transfer Confirmation",
  pinnedImportantInformation: "Pinned Important Information",
  passengerContact: "Passenger Contact",
  back: "Back",
  call: "Call",
  quickReply: "Quick Reply",
  customTemplates: "Custom Templates",
  noCustomTemplates: "No custom templates yet.",
  save: "Save",
  cancel: "Cancel",
  edit: "Edit",
  delete: "Delete",
  addCustomTemplate: "Add a custom template",
  addTemplate: "Add Template",
  serviceException: "Service Exception",
  serviceExceptions: "Service Exceptions",
  selectServiceException: "Select an issue",
  submitServiceException: "Submit issue",
  optionalAdminNote: "Optional note for admin",
  messages: "Messages",
  loadingEarlierMessages: "Loading earlier messages...",
  noMessages: "No messages yet.",
  messageRecalled: "This message was recalled.",
  translated: "Translated",
  chatAttachment: "Chat attachment",
  openLocation: "Open location",
  recall: "Recall",
  newMessages: "New messages",
  unreadMessages: "Unread messages below",
  backToLatest: "Latest",
  typeMessage: "Type your message",
  sending: "Sending...",
  send: "Send",
  attachImage: "Attach image",
  sendLocation: "Send location",
  invalidImage: "Please select an image file.",
  imageTooLarge: "Image must be under 2MB.",
  imageAttachmentMessage: "Image attachment",
  locationUnsupported: "Location sharing is not supported on this device.",
  locationUnavailable: "Unable to get your location.",
  locationMessage: "Location",
  roles: { system: "System", driver: "Driver", admin: "Admin", passenger: "Passenger" },
  serviceIssues: {
    cannot_find_passenger: "Cannot find passenger",
    flight_delay: "Flight delay",
    passenger_late: "Passenger late",
    location_changed: "Location changed",
    extra_luggage: "Extra luggage",
    vehicle_issue: "Vehicle issue",
    inappropriate_content: "Inappropriate message or harassment",
  },
};

function getTranslationForMessage(message, translations, preferredLanguage) {
  return translations.find(
    (translation) =>
      translation.message_id === message.id &&
      translation.target_language === preferredLanguage
  );
}

function mergeChatMessages(currentMessages = [], nextMessages = []) {
  const messageMap = new Map();
  [...currentMessages, ...nextMessages].forEach((message) => {
    if (message?.id) {
      messageMap.set(message.id, { ...messageMap.get(message.id), ...message });
    }
  });
  return Array.from(messageMap.values()).sort((left, right) => (
    String(left.created_at || "").localeCompare(String(right.created_at || ""))
  ));
}

function mergeChatTranslations(currentTranslations = [], nextTranslations = []) {
  const translationMap = new Map();
  [...currentTranslations, ...nextTranslations].forEach((translation) => {
    const key = translation?.id || `${translation?.message_id || ""}:${translation?.target_language || ""}`;
    if (key && translation?.message_id) {
      translationMap.set(key, { ...translationMap.get(key), ...translation });
    }
  });
  return Array.from(translationMap.values());
}

function getOrderSummary(order = {}) {
  const serviceType = String(order.service_type || "transfer").toLowerCase();
  return {
    orderNo: order.order_no || order.id || "-",
    date: order.departure_date || "-",
    time: order.pickup_time || "-",
    pickup: order.pickup_location_detail || order.pickup_location || order.pickup_area || "-",
    dropoff: order.dropoff_location_detail || order.destination || order.dropoff_area || order.end_location || "-",
    flight: order.flight_number || "-",
    passengers: order.passenger_count || 1,
    luggage: order.luggage_count ?? 0,
    vehicle: order.vehicle_type_v2 || order.vehicle_type || "-",
    serviceType,
    selectedHours: order.selected_hours || "",
  };
}

function normalizePhone(phone = "") {
  return String(phone || "").replace(/\D/g, "");
}

function formatReadAt(value, copy) {
  if (!value) return copy.notViewed;
  return new Date(value).toLocaleString(copy.locale);
}

function canRecallMessage(message, currentRole) {
  if (message.deleted_at) return false;
  return (currentRole === "admin" && message.sender_role === "admin") ||
    (currentRole === "driver" && message.sender_role === "driver");
}

function ChatMessageText({ value, className = "", onOpenLink }) {
  return (
    <p className={className}>
      {parseChatMessageLinks(value).map((segment, index) => (
        segment.type === "link" || segment.type === "phone" ? (
          <a
            key={`${segment.href}-${index}`}
            href={segment.href}
            target={segment.type === "link" ? "_blank" : undefined}
            rel={segment.type === "link" ? "noopener noreferrer" : undefined}
            onClick={(event) => onOpenLink(event, segment.href)}
            className="break-all font-semibold text-sky-700 underline decoration-sky-500/60 underline-offset-2 active:text-sky-900"
          >
            {segment.value}
          </a>
        ) : (
          <span key={`text-${index}`}>{segment.value}</span>
        )
      ))}
    </p>
  );
}

function getFeedbackCopy(language) {
  if (language === "ja") {
    return {
      creditBadge: "JPY 2,000 クレジット",
      title: "サービス評価で JPY 2,000 クレジット",
      description: "サービス完了後、専用評価リンクからご評価いただくと、会員アカウントに JPY 2,000 クレジットを追加できます。",
      waiting: "ご出発時刻以降に評価リンクが表示されます。",
      submitted: "評価ありがとうございます。旧クーポン情報は参照用として表示しています。",
      coupon: "クーポンコード",
      credit: "評価リンクを開く",
      google: "よろしければ Google Maps でも体験を共有できます。",
      openGoogle: "Google Maps を開く",
      formTitle: "サービスはいかがでしたか？",
      commentPlaceholder: "コメントは任意です",
      submit: "評価リンクを開く",
      submitting: "送信中...",
      ratingLabel: "サービス評価",
      lostItemTitle: "お忘れ物",
      lostItemReceived: "お忘れ物の報告を受け付けました。確認後にご連絡します。",
      lostItemDescription: "お忘れ物の特徴を入力",
      lostItemContact: "ご希望の連絡方法",
      submitLostItem: "お忘れ物を報告",
      lostItemReports: "お忘れ物の報告",
      contact: "連絡先",
    };
  }
  if (language === "zh" || language === "zh_cn") {
    return {
      creditBadge: "JPY 2,000 余额",
      title: "完成服务评价，领取 JPY 2,000 余额",
      description: "服务完成后，通过专属评价链接完成评价并验证，即可领取 JPY 2,000 会员余额。",
      waiting: "行程开始时间后，这里会显示评价链接。",
      submitted: "感谢您的评价。旧优惠券信息仅作为历史记录显示。",
      coupon: "优惠券代码",
      credit: "打开评价链接",
      google: "如果您愿意，也欢迎在 Google Maps 分享您的真实体验。",
      openGoogle: "打开 Google Maps",
      formTitle: "请为本次服务评分",
      commentPlaceholder: "评论可填可不填",
      submit: "打开评价链接",
      submitting: "提交中...",
      ratingLabel: "服务评分",
      lostItemTitle: "遗失物品",
      lostItemReceived: "遗失物品报告已收到。我们确认后会联系您。",
      lostItemDescription: "请描述遗失物品",
      lostItemContact: "首选联系方式",
      submitLostItem: "提交遗失物品报告",
      lostItemReports: "遗失物品报告",
      contact: "联系方式",
    };
  }
  if (language === "zh_tw") {
    return {
      creditBadge: "JPY 2,000 餘額",
      title: "完成服務評價，領取 JPY 2,000 餘額",
      description: "服務完成後，透過專屬評價連結完成評價並驗證，即可領取 JPY 2,000 會員餘額。",
      waiting: "行程開始時間後，這裡會顯示評價連結。",
      submitted: "感謝您的評價。舊優惠券資訊僅作為歷史記錄顯示。",
      coupon: "優惠券代碼",
      credit: "開啟評價連結",
      google: "如果您願意，也歡迎在 Google Maps 分享您的真實體驗。",
      openGoogle: "開啟 Google Maps",
      formTitle: "請為本次服務評分",
      commentPlaceholder: "評論可填可不填",
      submit: "開啟評價連結",
      submitting: "提交中...",
      ratingLabel: "服務評分",
      lostItemTitle: "遺失物品",
      lostItemReceived: "遺失物品報告已收到。我們確認後會聯絡您。",
      lostItemDescription: "請描述遺失物品",
      lostItemContact: "首選聯絡方式",
      submitLostItem: "提交遺失物品報告",
      lostItemReports: "遺失物品報告",
      contact: "聯絡方式",
    };
  }
  return {
    creditBadge: "JPY 2,000 CREDIT",
    title: "Complete service feedback and receive JPY 2,000 credit",
    description: "After your service, open the dedicated review link. Once verified, JPY 2,000 credit can be added to your member account.",
    waiting: "The review link will appear after your trip start time.",
    submitted: "Thank you for your feedback. Legacy coupon details are shown for reference only.",
    coupon: "Coupon code",
    credit: "Open review link",
    google: "If you would like, you may also share your experience on Google Maps.",
    openGoogle: "Open Google Maps",
    formTitle: "How was your service?",
    commentPlaceholder: "Optional comment",
    submit: "Open review link",
    submitting: "Submitting...",
    ratingLabel: "Service feedback rating",
    lostItemTitle: "Lost Item",
    lostItemReceived: "Lost item report received. We will review it and contact you.",
    lostItemDescription: "Describe the lost item",
    lostItemContact: "Best contact method",
    submitLostItem: "Submit lost item report",
    lostItemReports: "Lost Item Reports",
    contact: "Contact",
  };
}

export default function TripChatRoom({
  title = "",
  chatData,
  currentRole = "passenger",
  preferredLanguage,
  onPreferredLanguageChange,
  onSendMessage,
  onFetchMessages,
  onRecallMessage,
  sending,
  templates = [],
  customTemplates = [],
  showTemplates = false,
  onCreateTemplate,
  onUpdateTemplate,
  onDeleteTemplate,
  onSubmitFeedback,
  feedbackSubmitting = false,
  onReportServiceIssue,
  onSubmitLostItem,
  lostItemSubmitting = false,
  notificationNotice = "",
  onMessagesRead,
  respectSafeAreaTop = false,
  onBack,
  backLabel = "",
  driverMobileLayout = false,
  uiCopy = defaultUiCopy,
}) {
  const copy = {
    ...defaultUiCopy,
    ...uiCopy,
    roles: { ...defaultUiCopy.roles, ...(uiCopy?.roles || {}) },
    serviceIssues: { ...defaultUiCopy.serviceIssues, ...(uiCopy?.serviceIssues || {}) },
  };
  const roomTitle = title || copy.title;
  const resolvedBackLabel = backLabel || copy.back;
  const languageOptions = copy.languageOptions || defaultUiCopy.languageOptions;
  const serviceIssueOptions = Object.entries(copy.serviceIssues);
  const [messageBody, setMessageBody] = useState("");
  const [newTemplateBody, setNewTemplateBody] = useState("");
  const [editingTemplateId, setEditingTemplateId] = useState("");
  const [editingTemplateBody, setEditingTemplateBody] = useState("");
  const [templateSaving, setTemplateSaving] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackComment, setFeedbackComment] = useState("");
  const [issueNote, setIssueNote] = useState("");
  const [selectedServiceIssue, setSelectedServiceIssue] = useState("");
  const [serviceExceptionSubmitting, setServiceExceptionSubmitting] = useState(false);
  const [lostItemDescription, setLostItemDescription] = useState("");
  const [lostItemContact, setLostItemContact] = useState("");
  const [systemMessagesExpanded, setSystemMessagesExpanded] = useState(false);
  const [routeExpanded, setRouteExpanded] = useState(false);
  const [quickReplyExpanded, setQuickReplyExpanded] = useState(false);
  const [serviceExceptionExpanded, setServiceExceptionExpanded] = useState(false);
  const [messages, setMessages] = useState(chatData?.messages || []);
  const [translations, setTranslations] = useState(chatData?.translations || []);
  const [oldestCursor, setOldestCursor] = useState(chatData?.oldest_message_cursor || "");
  const [newestCursor, setNewestCursor] = useState(chatData?.newest_message_cursor || "");
  const [hasMoreBefore, setHasMoreBefore] = useState(Boolean(chatData?.has_more_before));
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [pollingNewer, setPollingNewer] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [newMessagesAvailable, setNewMessagesAvailable] = useState(false);
  const messagesScrollRef = useRef(null);
  const messageInputRef = useRef(null);
  const unreadDividerRef = useRef(null);
  const initialPositionAppliedRef = useRef(false);
  const initialUnreadBoundaryRef = useRef(
    chatData?.room?.viewer_had_unread
      ? chatData?.room?.viewer_last_read_at || ""
      : null
  );
  const oldestCursorRef = useRef("");
  const newestCursorRef = useRef("");
  const isAtBottomRef = useRef(true);
  const orderSummary = getOrderSummary(chatData?.order);
  const feedback = chatData?.feedback;
  const serviceEvents = chatData?.service_events || [];
  const lostItems = chatData?.lost_items || [];
  const feedbackCopy = getFeedbackCopy(preferredLanguage);
  const showPassengerFeedback = currentRole === "passenger";
  const contactPhone = chatData?.order?.phone || chatData?.order?.customer_phone || "";
  const normalizedPhone = normalizePhone(contactPhone);
  const systemMessageTitle = chatData?.confirmation_text?.split("\n")?.[0] || copy.transferConfirmation;
  const displayRoomTitle = driverMobileLayout
    ? chatData?.order?.customer_name || roomTitle
    : roomTitle;
  const firstUnreadMessageId = driverMobileLayout && initialUnreadBoundaryRef.current !== null
    ? messages.find((message) => {
      if (message.deleted_at || normalizeSenderRole(message, chatData?.order) === currentRole) return false;
      if (!initialUnreadBoundaryRef.current) return true;
      const messageAt = new Date(message.created_at).getTime();
      const boundaryAt = new Date(initialUnreadBoundaryRef.current).getTime();
      return Number.isFinite(messageAt) && Number.isFinite(boundaryAt) && messageAt > boundaryAt;
    })?.id || ""
    : "";

  const scrollMessagesToBottom = useCallback(() => {
    const element = messagesScrollRef.current;
    if (!element) return;
    element.scrollTop = element.scrollHeight;
    setIsAtBottom(true);
    isAtBottomRef.current = true;
    setNewMessagesAvailable(false);
  }, []);

  useEffect(() => {
    const initialMessages = chatData?.messages || [];
    const initialTranslations = chatData?.translations || [];
    const nextOldestCursor = chatData?.oldest_message_cursor || initialMessages[0]?.id || "";
    const nextNewestCursor = chatData?.newest_message_cursor || initialMessages[initialMessages.length - 1]?.id || "";

    setMessages(initialMessages);
    setTranslations(initialTranslations);
    setOldestCursor(nextOldestCursor);
    setNewestCursor(nextNewestCursor);
    setHasMoreBefore(Boolean(chatData?.has_more_before));
    oldestCursorRef.current = nextOldestCursor;
    newestCursorRef.current = nextNewestCursor;
    if (!initialPositionAppliedRef.current) {
      initialPositionAppliedRef.current = true;
      window.requestAnimationFrame(() => {
        const element = messagesScrollRef.current;
        const divider = unreadDividerRef.current;
        if (!driverMobileLayout || !element || !divider) {
          scrollMessagesToBottom();
          return;
        }
        element.scrollTop = Math.max(0, divider.offsetTop - 12);
        const nextIsAtBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 72;
        setIsAtBottom(nextIsAtBottom);
        isAtBottomRef.current = nextIsAtBottom;
        setNewMessagesAvailable(false);
      });
    }
  }, [
    chatData?.room?.id,
    chatData?.messages,
    chatData?.translations,
    chatData?.has_more_before,
    chatData?.oldest_message_cursor,
    chatData?.newest_message_cursor,
    driverMobileLayout,
    preferredLanguage,
    scrollMessagesToBottom,
  ]);

  const appendCreatedMessage = useCallback((createdMessage) => {
    if (!createdMessage?.id) return;
    setMessages((currentMessages) => mergeChatMessages(currentMessages, [createdMessage]));
    setNewestCursor(createdMessage.id);
    newestCursorRef.current = createdMessage.id;
    window.requestAnimationFrame(scrollMessagesToBottom);
  }, [scrollMessagesToBottom]);

  const loadOlderMessages = useCallback(async () => {
    if (!onFetchMessages || loadingOlder || !hasMoreBefore || !oldestCursorRef.current) return;
    const element = messagesScrollRef.current;
    const previousScrollHeight = element?.scrollHeight || 0;
    const previousScrollTop = element?.scrollTop || 0;

    setLoadingOlder(true);
    try {
      const page = await onFetchMessages({
        limit: 30,
        beforeMessageId: oldestCursorRef.current,
      });
      const pageMessages = page?.messages || [];
      setMessages((currentMessages) => mergeChatMessages(currentMessages, pageMessages));
      setTranslations((currentTranslations) => mergeChatTranslations(currentTranslations, page?.translations || []));
      const nextOldestCursor = page?.oldest_message_cursor || pageMessages[0]?.id || oldestCursorRef.current;
      setOldestCursor(nextOldestCursor);
      oldestCursorRef.current = nextOldestCursor;
      setHasMoreBefore(Boolean(page?.has_more_before));
      window.requestAnimationFrame(() => {
        const nextElement = messagesScrollRef.current;
        if (!nextElement) return;
        nextElement.scrollTop = nextElement.scrollHeight - previousScrollHeight + previousScrollTop;
      });
    } finally {
      setLoadingOlder(false);
    }
  }, [hasMoreBefore, loadingOlder, onFetchMessages]);

  const pollNewMessages = useCallback(async () => {
    if (!onFetchMessages || pollingNewer || !newestCursorRef.current) return;
    setPollingNewer(true);
    try {
      const page = await onFetchMessages({
        limit: 30,
        afterMessageId: newestCursorRef.current,
      });
      const pageMessages = page?.messages || [];
      if (pageMessages.length === 0) return;
      setMessages((currentMessages) => mergeChatMessages(currentMessages, pageMessages));
      setTranslations((currentTranslations) => mergeChatTranslations(currentTranslations, page?.translations || []));
      const nextNewestCursor = page?.newest_message_cursor || pageMessages[pageMessages.length - 1]?.id || newestCursorRef.current;
      setNewestCursor(nextNewestCursor);
      newestCursorRef.current = nextNewestCursor;
      if (isAtBottomRef.current) {
        window.requestAnimationFrame(scrollMessagesToBottom);
      } else {
        setNewMessagesAvailable(true);
      }
    } finally {
      setPollingNewer(false);
    }
  }, [onFetchMessages, pollingNewer, scrollMessagesToBottom]);

  useEffect(() => {
    if (isAtBottom && !newMessagesAvailable) onMessagesRead?.();
  }, [isAtBottom, newMessagesAvailable, messages, notificationNotice, onMessagesRead]);

  const handleMessagesScroll = useCallback(() => {
    const element = messagesScrollRef.current;
    if (!element) return;
    if (element.scrollTop < 48) {
      loadOlderMessages();
    }
    const nextIsAtBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 72;
    setIsAtBottom(nextIsAtBottom);
    isAtBottomRef.current = nextIsAtBottom;
    if (nextIsAtBottom) {
      setNewMessagesAvailable(false);
    }
  }, [loadOlderMessages]);

  useEffect(() => {
    if (!onFetchMessages) return undefined;
    const timer = window.setInterval(() => {
      pollNewMessages();
    }, 15000);
    return () => window.clearInterval(timer);
  }, [onFetchMessages, pollNewMessages]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const body = messageBody.trim();
    if (!body) return;
    const createdMessage = await onSendMessage(body, preferredLanguage);
    appendCreatedMessage(createdMessage);
    setMessageBody("");
  };

  const handleSelectTemplate = (body) => {
    setMessageBody(body);
    if (driverMobileLayout) setQuickReplyExpanded(false);
    window.requestAnimationFrame(() => messageInputRef.current?.focus());
  };

  const handleOpenChatLink = useCallback((event, url) => {
    if (url.startsWith("tel:")) return;
    if (!Capacitor.isNativePlatform()) return;
    event.preventDefault();
    Browser.open({ url }).catch(() => window.location.assign(url));
  }, []);

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      alert(copy.invalidImage);
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      alert(copy.imageTooLarge);
      return;
    }
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    const createdMessage = await onSendMessage(copy.imageAttachmentMessage, preferredLanguage, [{
      type: "image",
      name: file.name,
      mime_type: file.type,
      data_url: dataUrl,
    }]);
    appendCreatedMessage(createdMessage);
  };

  const handleSendLocation = async () => {
    if (!navigator.geolocation) {
      alert(copy.locationUnsupported);
      return;
    }
    navigator.geolocation.getCurrentPosition(async (position) => {
      const { latitude, longitude } = position.coords;
      const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
      const createdMessage = await onSendMessage(`${copy.locationMessage}: ${url}`, preferredLanguage, [{
        type: "location",
        latitude,
        longitude,
        url,
      }]);
      appendCreatedMessage(createdMessage);
    }, () => {
      alert(copy.locationUnavailable);
    }, { enableHighAccuracy: true, timeout: 10000 });
  };

  const handleCreateTemplate = async () => {
    const body = newTemplateBody.trim();
    if (!body || !onCreateTemplate) return;
    setTemplateSaving(true);
    try {
      await onCreateTemplate(body);
      setNewTemplateBody("");
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleUpdateTemplate = async (templateId) => {
    const body = editingTemplateBody.trim();
    if (!body || !onUpdateTemplate) return;
    setTemplateSaving(true);
    try {
      await onUpdateTemplate(templateId, body);
      setEditingTemplateId("");
      setEditingTemplateBody("");
    } finally {
      setTemplateSaving(false);
    }
  };

  const startEditingTemplate = (template) => {
    setEditingTemplateId(template.id);
    setEditingTemplateBody(template.body);
  };

  const handleFeedbackSubmit = async () => {
    if (!onSubmitFeedback || feedbackSubmitting) return;
    await onSubmitFeedback(feedbackRating, feedbackComment);
    setFeedbackComment("");
  };

  const handleLostItemSubmit = async () => {
    if (!onSubmitLostItem || !lostItemDescription.trim()) return;
    await onSubmitLostItem(lostItemDescription, lostItemContact);
    setLostItemDescription("");
    setLostItemContact("");
  };

  const handleSubmitServiceIssue = async () => {
    if (!selectedServiceIssue || serviceExceptionSubmitting) return;
    setServiceExceptionSubmitting(true);
    try {
      await onReportServiceIssue(selectedServiceIssue, issueNote);
      setSelectedServiceIssue("");
      setIssueNote("");
      setServiceExceptionExpanded(false);
    } finally {
      setServiceExceptionSubmitting(false);
    }
  };

  return (
    <div className={`trip-chat-luxury min-h-screen bg-black text-white ${driverMobileLayout ? "trip-chat-mobile-shell" : ""}`}>
      <div className={`sticky top-0 z-10 border-b border-[#d8b769]/20 bg-black/85 px-4 py-3 backdrop-blur-xl ${respectSafeAreaTop ? "trip-chat-safe-header" : ""} ${driverMobileLayout ? "trip-chat-mobile-header" : ""}`}>
        <div className={`mx-auto flex max-w-4xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between ${driverMobileLayout ? "trip-chat-mobile-header-inner" : ""}`}>
          <div className={`flex items-center gap-3 ${driverMobileLayout ? "trip-chat-mobile-header-identity" : ""}`}>
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className={`inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-[#d8b769]/35 bg-[#0c0a07] text-[#f6d982] shadow-[0_12px_34px_rgba(216,183,105,0.12)] transition active:scale-95 ${driverMobileLayout ? "trip-chat-mobile-back" : ""}`}
                data-testid="trip-chat-back-button"
                aria-label={resolvedBackLabel}
              >
                <ChevronLeft className="h-7 w-7" />
              </button>
            )}
            <span className={`shonan-brand-logo-unframed inline-flex h-12 w-12 items-center justify-center ${driverMobileLayout ? "trip-chat-mobile-logo" : ""}`}>
              <img src={shonanPalmLogo} alt="SHONAN" className="h-9 w-9 object-contain" />
            </span>
            <div className={driverMobileLayout ? "trip-chat-mobile-title-box" : ""}>
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-semibold tracking-tight text-white">{displayRoomTitle}</h1>
                <MessageCircle className="h-4 w-4 text-[#d8b769]" />
              </div>
              {!driverMobileLayout && (
                <>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#d8b769]">SHONAN INTERNATIONAL</p>
                  <p className="text-xs font-medium text-white/85">Private Transfer & Charter</p>
                </>
              )}
            </div>
          </div>
          {driverMobileLayout ? (
            <div className="trip-chat-mobile-header-actions">
              <select
                value={preferredLanguage}
                onChange={(event) => onPreferredLanguageChange(event.target.value)}
                className="trip-chat-mobile-language"
                aria-label={copy.preferredLanguage}
              >
                {languageOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => {
                  setQuickReplyExpanded(false);
                  setServiceExceptionExpanded((expanded) => !expanded);
                }}
                className={`trip-chat-mobile-service-alert ${serviceExceptionExpanded ? "is-active" : ""}`}
                aria-label={copy.serviceException}
                aria-expanded={serviceExceptionExpanded}
                data-testid="trip-chat-service-alert"
              >
                <span aria-hidden="true">🚨</span>
              </button>
            </div>
          ) : (
            <select
              value={preferredLanguage}
              onChange={(event) => onPreferredLanguageChange(event.target.value)}
              className="rounded-xl border border-[#d8b769]/30 bg-[#0c0a07] px-3 py-2 text-sm font-semibold text-[#f6d982] outline-none focus:ring-2 focus:ring-[#d8b769]/45"
              aria-label={copy.preferredLanguage}
            >
              {languageOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      <main className={`mx-auto flex max-w-4xl flex-col gap-4 px-4 py-4 ${driverMobileLayout ? "trip-chat-mobile-main" : ""}`}>
        <section className={`trip-chat-card rounded-2xl border border-[#d8b769]/20 bg-[#0c0a07]/90 p-4 shadow-2xl shadow-black/40 ${driverMobileLayout ? "trip-chat-mobile-order-summary" : ""}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            {driverMobileLayout ? (
              <div className="trip-chat-mobile-summary-line">
                <strong>{orderSummary.date} {orderSummary.time}</strong>
                <span>
                  {copy.service}: {copy.serviceTypes?.[orderSummary.serviceType] || orderSummary.serviceType}
                  {orderSummary.serviceType === "charter" && orderSummary.selectedHours ? ` · ${orderSummary.selectedHours} ${copy.hours}` : ""}
                </span>
                <span>{copy.order}: {orderSummary.orderNo}</span>
              </div>
            ) : (
              <div>
                <h2 className="text-sm font-semibold text-[#d8b769]">{copy.orderSummary}</h2>
                <p className="mt-1 text-lg font-semibold text-white">{orderSummary.date} {orderSummary.time}</p>
                <p className="mt-1 text-sm font-semibold text-[#f6d982]">
                  {copy.service}: {copy.serviceTypes?.[orderSummary.serviceType] || orderSummary.serviceType}
                  {orderSummary.serviceType === "charter" && orderSummary.selectedHours ? ` · ${orderSummary.selectedHours} ${copy.hours}` : ""}
                </p>
                <p className="text-xs text-white/45">{copy.order}: {orderSummary.orderNo}</p>
              </div>
            )}
            <div className={`rounded-xl border border-[#d8b769]/20 bg-[#d8b769]/[0.06] px-3 py-2 text-right text-xs text-white/65 ${driverMobileLayout ? "trip-chat-mobile-order-meta" : ""}`}>
              <div>{copy.flight}: {orderSummary.flight}</div>
              <div>{copy.vehicle}: {orderSummary.vehicle}</div>
              <div>{copy.passengers}: {orderSummary.passengers} · {copy.luggage}: {orderSummary.luggage}</div>
            </div>
          </div>
          {driverMobileLayout && (
            <button
              type="button"
              onClick={() => setRouteExpanded((expanded) => !expanded)}
              className="trip-chat-mobile-route-summary"
              aria-expanded={routeExpanded}
              data-testid="trip-chat-route-summary"
            >
              <span>{orderSummary.pickup}</span>
              <ArrowRight aria-hidden="true" />
              <span>{orderSummary.dropoff}</span>
              {routeExpanded ? <ChevronUp aria-hidden="true" /> : <ChevronDown aria-hidden="true" />}
            </button>
          )}
          {(!driverMobileLayout || routeExpanded) && (
            <div className={`mt-3 grid gap-2 text-sm sm:grid-cols-2 ${driverMobileLayout ? "trip-chat-mobile-route-grid" : ""}`}>
              <div className={`rounded-xl border border-[#d8b769]/15 bg-white/[0.04] p-3 ${driverMobileLayout ? "trip-chat-mobile-route" : ""}`}>
                <div className="text-xs font-semibold text-[#d8b769]">{copy.pickup}</div>
                <div className="mt-1 break-words text-white/86">{orderSummary.pickup}</div>
              </div>
              <div className={`rounded-xl border border-[#d8b769]/15 bg-white/[0.04] p-3 ${driverMobileLayout ? "trip-chat-mobile-route" : ""}`}>
                <div className="text-xs font-semibold text-[#d8b769]">{copy.dropoff}</div>
                <div className="mt-1 break-words text-white/86">{orderSummary.dropoff}</div>
              </div>
            </div>
          )}
          <div className={`mt-3 grid gap-2 rounded-xl border border-white/10 bg-black/35 p-3 text-xs text-white/55 sm:grid-cols-3 ${driverMobileLayout ? "trip-chat-mobile-read-state" : ""}`}>
            <div>
              <span className="font-semibold">{copy.passengerLastViewed}:</span>
              <div>{formatReadAt(chatData?.room?.last_read_at_by_passenger, copy)}</div>
            </div>
            <div>
              <span className="font-semibold">{copy.driverLastViewed}:</span>
              <div>{formatReadAt(chatData?.room?.last_read_at_by_driver, copy)}</div>
            </div>
            <div>
              <span className="font-semibold">{copy.adminLastViewed}:</span>
              <div>{formatReadAt(chatData?.room?.last_read_at_by_admin, copy)}</div>
            </div>
          </div>
        </section>

        {driverMobileLayout && (
          <section className="trip-chat-mobile-contact-bar" data-testid="trip-chat-mobile-contact-bar">
            <div className="trip-chat-mobile-contact-copy">
              <span>{copy.passengerContact}</span>
              {contactPhone && <strong>{contactPhone}</strong>}
            </div>
            <div className="trip-chat-mobile-contact-actions">
              {normalizedPhone && (
                <>
                  <a href={`tel:${normalizedPhone}`} aria-label={`${copy.call} ${contactPhone}`}>
                    <Phone className="h-4 w-4" />
                  </a>
                  <a href={`https://wa.me/${normalizedPhone}`} target="_blank" rel="noreferrer" aria-label={`WhatsApp ${contactPhone}`}>
                    <MessageCircle className="h-4 w-4" />
                  </a>
                </>
              )}
            </div>
          </section>
        )}

        {showPassengerFeedback && (
          <section className="overflow-hidden rounded-2xl border border-amber-300 bg-gradient-to-br from-amber-100 via-orange-50 to-yellow-50 p-4 shadow-[0_16px_36px_rgba(217,119,6,0.18)]">
            <div className="flex items-start gap-3">
              <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber-500 text-white shadow-md">
                <Gift className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="inline-flex rounded-full bg-white/75 px-3 py-1 text-xs font-bold text-amber-800">
                  {feedbackCopy.creditBadge}
                </div>
                <h2 className="mt-2 text-lg font-bold text-stone-950">{feedbackCopy.title}</h2>
                <p className="mt-1 text-sm leading-6 text-stone-700">{feedbackCopy.description}</p>

                {feedback ? (
                  <div className="mt-3 rounded-xl border border-amber-200 bg-white/80 p-3 text-sm text-stone-700">
                    <div className="font-semibold text-amber-900">{feedbackCopy.submitted}</div>
                    {feedback.coupon_code && (
                      <div className="mt-2 inline-flex items-center gap-2 rounded-lg bg-amber-500 px-3 py-2 font-bold text-white">
                        <span>{feedbackCopy.coupon}</span>
                        <span>{feedback.coupon_code}</span>
                      </div>
                    )}
                    {feedback.rating >= 5 && feedback.google_review_url && (
                      <div className="mt-3">
                        <p>{feedbackCopy.google}</p>
                        <a
                          href={feedback.google_review_url}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-2 inline-flex items-center gap-2 rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-50"
                        >
                          <ExternalLink className="h-4 w-4" />
                          {feedbackCopy.openGoogle}
                        </a>
                      </div>
                    )}
                  </div>
                ) : chatData?.feedback_available && chatData?.review_invitation_url ? (
                  <div className="mt-3 rounded-xl border border-amber-200 bg-white/80 p-3">
                    <div className="text-sm font-semibold text-stone-700">{feedbackCopy.formTitle}</div>
                    <a
                      href={chatData.review_invitation_url}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-3 inline-flex items-center justify-center gap-2 rounded-xl bg-amber-600 px-4 py-2 text-sm font-bold text-white shadow-sm"
                    >
                      <Gift className="h-4 w-4" />
                      {feedbackCopy.credit}
                    </a>
                  </div>
                ) : chatData?.feedback_available ? (
                  <div className="mt-3 rounded-xl border border-amber-200 bg-white/80 p-3">
                    <div className="text-sm font-semibold text-stone-700">{feedbackCopy.formTitle}</div>
                    <div className="mt-2 flex gap-1" aria-label={feedbackCopy.ratingLabel}>
                      {[1, 2, 3, 4, 5].map((value) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setFeedbackRating(value)}
                          className={`rounded-lg p-1 transition ${value <= feedbackRating ? "scale-105 text-amber-500" : "text-stone-300 hover:text-amber-300"}`}
                        >
                          <Star className="h-7 w-7 fill-current" />
                        </button>
                      ))}
                    </div>
                    {feedbackRating > 0 && (
                      <>
                        <textarea
                          value={feedbackComment}
                          onChange={(event) => setFeedbackComment(event.target.value)}
                          placeholder={feedbackCopy.commentPlaceholder}
                          className="mt-3 min-h-[76px] w-full rounded-xl border border-amber-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-500"
                        />
                        <button
                          type="button"
                          onClick={handleFeedbackSubmit}
                          disabled={feedbackSubmitting}
                          className="mt-3 inline-flex items-center justify-center gap-2 rounded-xl bg-amber-600 px-4 py-2 text-sm font-bold text-white shadow-sm disabled:opacity-50"
                        >
                          <Gift className="h-4 w-4" />
                          {feedbackSubmitting ? feedbackCopy.submitting : feedbackCopy.submit}
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="mt-3 rounded-xl bg-white/70 px-3 py-2 text-sm font-semibold text-amber-900">
                    {feedbackCopy.waiting}
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {driverMobileLayout && chatData?.confirmation_text ? (
          <section
            data-driver-mobile-section="system"
            className="order-1 rounded-2xl border border-[#d8b769]/20 bg-[#0c0a07]/90 shadow-sm"
          >
            <button
              type="button"
              onClick={() => setSystemMessagesExpanded((expanded) => !expanded)}
              className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left"
            >
              <div className="min-w-0">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#d8b769]">{copy.system}</div>
                <div className="mt-1 truncate text-sm font-semibold text-white/86">{systemMessageTitle}</div>
              </div>
              {systemMessagesExpanded ? (
                <ChevronUp className="h-5 w-5 shrink-0 text-[#f6d982]" />
              ) : (
                <ChevronDown className="h-5 w-5 shrink-0 text-[#f6d982]" />
              )}
            </button>
            {systemMessagesExpanded && (
              <div className="border-t border-[#d8b769]/15 p-4">
                <TripConfirmationCard confirmationText={chatData?.confirmation_text} systemLabel={copy.system} fallbackTitle={copy.transferConfirmation} />
              </div>
            )}
          </section>
        ) : (
          <TripConfirmationCard confirmationText={chatData?.confirmation_text} systemLabel={copy.system} fallbackTitle={copy.transferConfirmation} />
        )}

        {chatData?.room?.pinned_note && (
          <section className={`rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 shadow-sm ${driverMobileLayout ? "order-1" : ""}`}>
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <div className="font-semibold">{copy.pinnedImportantInformation}</div>
                <p className="mt-1 whitespace-pre-wrap break-words">{chatData.room.pinned_note}</p>
              </div>
            </div>
          </section>
        )}

        {!driverMobileLayout && (currentRole === "admin" || currentRole === "driver") && normalizedPhone && (
          <section className={`rounded-2xl border border-emerald-100 bg-white p-4 shadow-sm ${driverMobileLayout ? "order-5" : ""}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold text-stone-600">{copy.passengerContact}</h2>
                <p className="mt-1 text-sm text-stone-500">{contactPhone}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <a
                  href={`tel:${normalizedPhone}`}
                  className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 px-3 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-50"
                >
                  <Phone className="h-4 w-4" />
                  {copy.call}
                </a>
                <a
                  href={`https://wa.me/${normalizedPhone}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-3 py-2 text-sm font-semibold text-white"
                >
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp
                </a>
              </div>
            </div>
          </section>
        )}

        {showTemplates && templates.length > 0 && (
          <section data-driver-mobile-section="quick-reply" data-expanded={quickReplyExpanded ? "true" : "false"} className={`rounded-2xl border border-amber-100 bg-white p-4 shadow-sm ${driverMobileLayout ? "order-3" : ""}`}>
            <button
              type="button"
              onClick={() => driverMobileLayout && setQuickReplyExpanded((expanded) => !expanded)}
              className="flex w-full items-center justify-between gap-3 text-left"
            >
              <h2 className="text-sm font-semibold text-stone-500">{copy.quickReply}</h2>
              {driverMobileLayout && (
                quickReplyExpanded ? (
                  <ChevronUp className="h-5 w-5 shrink-0 text-stone-500" />
                ) : (
                  <ChevronDown className="h-5 w-5 shrink-0 text-stone-500" />
                )
              )}
            </button>
            {(!driverMobileLayout || quickReplyExpanded) && (
              <>
            <div className="mt-3 flex flex-wrap gap-2">
              {templates.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => handleSelectTemplate(template.body)}
                  className="rounded-xl border border-amber-100 bg-amber-50 px-3 py-2 text-left text-sm text-stone-800 hover:bg-amber-100"
                >
                  {template.body}
                </button>
              ))}
            </div>

            <div className="mt-4 border-t border-amber-100 pt-4">
              <h3 className="text-sm font-semibold text-stone-500">{copy.customTemplates}</h3>
              <div className="mt-3 flex flex-col gap-2">
                {customTemplates.length === 0 ? (
                  <div className="text-xs text-muted-foreground">{copy.noCustomTemplates}</div>
                ) : (
                  customTemplates.map((template) => (
                    <div key={template.id} className="rounded-xl border border-amber-100 bg-[#fffaf2] p-3">
                      {editingTemplateId === template.id ? (
                        <div className="flex flex-col gap-2">
                          <textarea
                            value={editingTemplateBody}
                            onChange={(event) => setEditingTemplateBody(event.target.value)}
                            className="min-h-[72px] rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm"
                          />
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => handleUpdateTemplate(template.id)}
                              disabled={templateSaving || !editingTemplateBody.trim()}
                              className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50"
                            >
                              <Save className="h-3.5 w-3.5" />
                              {copy.save}
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingTemplateId("")}
                              className="rounded-lg border border-amber-200 px-3 py-2 text-xs font-semibold"
                            >
                              {copy.cancel}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                          <button
                            type="button"
                            onClick={() => handleSelectTemplate(template.body)}
                            className="whitespace-pre-wrap break-words text-left text-sm hover:text-primary"
                          >
                            {template.body}
                          </button>
                          <div className="flex shrink-0 gap-2">
                            <button
                              type="button"
                              onClick={() => startEditingTemplate(template)}
                              className="inline-flex items-center gap-1 rounded-lg border border-amber-200 px-2 py-1 text-xs font-semibold hover:bg-amber-50"
                            >
                              <Edit2 className="h-3.5 w-3.5" />
                              {copy.edit}
                            </button>
                            <button
                              type="button"
                              onClick={() => onDeleteTemplate?.(template.id)}
                              className="inline-flex items-center gap-1 rounded-lg border border-rose-200 px-2 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-50"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              {copy.delete}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
              <div className="mt-3 flex flex-col gap-2">
                <textarea
                  value={newTemplateBody}
                  onChange={(event) => setNewTemplateBody(event.target.value)}
                  placeholder={copy.addCustomTemplate}
                  className="min-h-[72px] rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={handleCreateTemplate}
                  disabled={templateSaving || !newTemplateBody.trim()}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50 sm:self-start"
                >
                  <Plus className="h-4 w-4" />
                  {copy.addTemplate}
                </button>
              </div>
            </div>
              </>
            )}
          </section>
        )}

        {currentRole === "driver" && onReportServiceIssue && (!driverMobileLayout || serviceExceptionExpanded) && (
          <div className={driverMobileLayout ? "trip-chat-mobile-service-overlay" : "contents"}>
          {driverMobileLayout && (
            <button
              type="button"
              className="trip-chat-mobile-service-backdrop"
              onClick={() => setServiceExceptionExpanded(false)}
              aria-label={copy.cancel}
            />
          )}
          <section
            data-driver-mobile-section="service-exception"
            className={`rounded-2xl border border-rose-100 bg-white p-4 shadow-sm ${driverMobileLayout ? "order-4" : ""}`}
            role={driverMobileLayout ? "dialog" : undefined}
            aria-modal={driverMobileLayout ? "true" : undefined}
            aria-labelledby="trip-chat-service-exception-title"
          >
            <div className="trip-chat-mobile-service-dialog-header">
              <div>
                {driverMobileLayout && <span aria-hidden="true">🚨</span>}
                <h2 id="trip-chat-service-exception-title" className="text-sm font-semibold text-stone-600">{copy.serviceException}</h2>
              </div>
              {driverMobileLayout && (
                <button
                  type="button"
                  onClick={() => setServiceExceptionExpanded(false)}
                  aria-label={copy.cancel}
                >
                  <X aria-hidden="true" />
                </button>
              )}
            </div>
            {(!driverMobileLayout || serviceExceptionExpanded) && (
              <div className="trip-chat-mobile-service-dialog-body">
            {driverMobileLayout && <p>{copy.selectServiceException}</p>}
            <div className="trip-chat-service-issue-options mt-3 flex flex-wrap gap-2">
              {serviceIssueOptions.map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={async () => {
                    if (driverMobileLayout) {
                      setSelectedServiceIssue(value);
                      return;
                    }
                    await onReportServiceIssue(value, issueNote);
                  }}
                  className={`rounded-xl border border-rose-100 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-800 hover:bg-rose-100 ${selectedServiceIssue === value ? "is-selected" : ""}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <textarea
              value={issueNote}
              onChange={(event) => setIssueNote(event.target.value)}
              placeholder={copy.optionalAdminNote}
              className="trip-chat-service-issue-note mt-3 min-h-[68px] w-full rounded-xl border border-rose-100 bg-white px-3 py-2 text-sm"
            />
            {driverMobileLayout && serviceEvents.length > 0 && (
              <div className="mt-4 border-t border-rose-200/30 pt-4">
                <h3 className="text-sm font-semibold text-rose-200">{copy.serviceExceptions}</h3>
                <div className="mt-2 space-y-2">
                  {serviceEvents.slice(0, 3).map((event) => (
                    <div key={event.id} className="rounded-xl bg-white/5 p-3 text-sm text-white/80">
                      <div className="font-semibold text-rose-200">{copy.serviceIssues[event.issue_type] || event.issue_type}</div>
                      {event.note && <p className="mt-1 whitespace-pre-wrap break-words text-white/55">{event.note}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
              </div>
            )}
            {driverMobileLayout && (
              <div className="trip-chat-mobile-service-dialog-footer">
                <button
                  type="button"
                  onClick={handleSubmitServiceIssue}
                  disabled={!selectedServiceIssue || serviceExceptionSubmitting}
                  data-testid="trip-chat-service-exception-submit"
                >
                  <Send aria-hidden="true" />
                  {serviceExceptionSubmitting ? copy.sending : copy.submitServiceException}
                </button>
              </div>
            )}
          </section>
          </div>
        )}

        {serviceEvents.length > 0 && !driverMobileLayout && (
          <section className={`rounded-2xl border border-rose-100 bg-rose-50 p-4 shadow-sm ${driverMobileLayout ? "order-5" : ""}`}>
            <h2 className="text-sm font-semibold text-rose-800">{copy.serviceExceptions}</h2>
            <div className="mt-2 space-y-2">
              {serviceEvents.slice(0, 3).map((event) => (
                <div key={event.id} className="rounded-xl bg-white p-3 text-sm text-stone-700">
                  <div className="font-semibold">{copy.serviceIssues[event.issue_type] || event.issue_type}</div>
                  {event.note && <p className="mt-1 whitespace-pre-wrap break-words text-stone-500">{event.note}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {currentRole === "passenger" && (
          <section className="rounded-2xl border border-sky-100 bg-white p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-stone-600">{feedbackCopy.lostItemTitle}</h2>
            {lostItems.length > 0 && (
              <div className="mt-2 rounded-xl bg-sky-50 p-3 text-sm text-sky-900">
                {feedbackCopy.lostItemReceived}
              </div>
            )}
            <div className="mt-3 grid gap-2">
              <textarea
                value={lostItemDescription}
                onChange={(event) => setLostItemDescription(event.target.value)}
                placeholder={feedbackCopy.lostItemDescription}
                className="min-h-[72px] rounded-xl border border-sky-100 bg-white px-3 py-2 text-sm"
              />
              <input
                value={lostItemContact}
                onChange={(event) => setLostItemContact(event.target.value)}
                placeholder={feedbackCopy.lostItemContact}
                className="rounded-xl border border-sky-100 bg-white px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={handleLostItemSubmit}
                disabled={lostItemSubmitting || !lostItemDescription.trim()}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-sky-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50 sm:self-start"
              >
                {feedbackCopy.submitLostItem}
              </button>
            </div>
          </section>
        )}

        {lostItems.length > 0 && currentRole === "admin" && (
          <section className="rounded-2xl border border-sky-100 bg-sky-50 p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-sky-900">{feedbackCopy.lostItemReports}</h2>
            <div className="mt-2 space-y-2">
              {lostItems.map((item) => (
                <div key={item.id} className="rounded-xl bg-white p-3 text-sm text-stone-700">
                  <p className="whitespace-pre-wrap break-words">{item.item_description}</p>
                  {item.contact_info && <p className="mt-1 text-stone-500">{feedbackCopy.contact}: {item.contact_info}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        <section
          data-driver-mobile-section="message"
          data-oldest-cursor={oldestCursor}
          data-newest-cursor={newestCursor}
          data-at-bottom={isAtBottom ? "true" : "false"}
          className={`overflow-hidden rounded-2xl border border-amber-100 bg-white shadow-sm ${driverMobileLayout ? "order-2 trip-chat-mobile-message-card" : ""}`}
        >
          <div className={`border-b border-amber-100 bg-amber-50/70 px-4 py-3 ${driverMobileLayout ? "trip-chat-mobile-message-heading" : ""}`}>
            <h2 className="text-sm font-semibold text-stone-600">{copy.messages}</h2>
          </div>
          {notificationNotice && (
            <div className={`border-b border-sky-100 bg-sky-50 px-4 py-2 text-xs font-semibold text-sky-800 ${driverMobileLayout ? "trip-chat-mobile-notice" : ""}`}>
              {notificationNotice}
            </div>
          )}
          <div
            ref={messagesScrollRef}
            onScroll={handleMessagesScroll}
            className={`relative flex h-[56vh] min-h-[360px] flex-col gap-4 overflow-y-auto bg-[#fff7ec] p-4 ${driverMobileLayout ? "trip-chat-mobile-message-list" : ""}`}
          >
            {loadingOlder && (
              <div className="text-center text-xs font-semibold text-stone-400">
                {copy.loadingEarlierMessages}
              </div>
            )}
            {messages.length === 0 ? (
              <div className="flex flex-1 items-center justify-center text-sm text-stone-500">
                {copy.noMessages}
              </div>
            ) : (
              messages.map((message) => {
                const translation = getTranslationForMessage(message, translations, preferredLanguage);
                const normalizedRole = normalizeSenderRole(message, chatData?.order);
                const meta = getRoleMeta(normalizedRole, copy.roles);
                const deleted = Boolean(message.deleted_at);
                const isOwnMessage = normalizedRole === currentRole;
                const mobileAlignment = normalizedRole === "system" ? "items-center is-system" : isOwnMessage ? "items-end is-own" : "items-start is-incoming";
                return (
                  <Fragment key={message.id}>
                  {message.id === firstUnreadMessageId && (
                    <div
                      ref={unreadDividerRef}
                      className="trip-chat-mobile-unread-divider"
                      data-testid="trip-chat-unread-divider"
                    >
                      <span>{copy.unreadMessages}</span>
                    </div>
                  )}
                  <article className={`trip-chat-message-row flex flex-col ${driverMobileLayout ? mobileAlignment : meta.align}`}>
                    <div className={`trip-chat-message-content ${meta.max} min-w-[220px]`}>
                      <div className="trip-chat-message-meta mb-1 flex flex-wrap items-center gap-2">
                        <span className={`trip-chat-message-role rounded-full border px-2 py-0.5 text-[11px] font-semibold ${meta.chip}`}>
                          {meta.label}
                        </span>
                        <span className="text-xs font-semibold text-stone-600">{getDisplayName(message, chatData?.order, copy.roles)}</span>
                        <span className="text-[11px] text-stone-400">
                          {message.created_at ? new Date(message.created_at).toLocaleString(copy.locale) : ""}
                        </span>
                      </div>
                      <div className={`trip-chat-message-bubble rounded-2xl border px-4 py-3 shadow-sm ${deleted ? "is-deleted border-stone-200 bg-stone-50 text-stone-500" : meta.bubble}`}>
                        {deleted ? (
                          <p className="text-sm italic">{copy.messageRecalled}</p>
                        ) : (
                          <>
                            <ChatMessageText
                              value={message.body}
                              onOpenLink={handleOpenChatLink}
                              className="whitespace-pre-wrap break-words text-sm leading-6"
                            />
                            {translation && translation.translated_body !== message.body && (
                              <div className="trip-chat-message-translation mt-3 rounded-xl bg-white/70 p-3 text-sm">
                                <div className="text-xs font-semibold text-stone-500">{copy.translated}</div>
                                <ChatMessageText
                                  value={translation.translated_body}
                                  onOpenLink={handleOpenChatLink}
                                  className="mt-1 whitespace-pre-wrap break-words"
                                />
                              </div>
                            )}
                            {message.attachments?.length > 0 && (
                              <div className="mt-3 flex flex-col gap-2">
                                {message.attachments.map((attachment, index) => (
                                  <div key={`${message.id}-attachment-${index}`}>
                                    {attachment.type === "image" && attachment.data_url && (
                                      <img
                                        src={attachment.data_url}
                                        alt={attachment.name || copy.chatAttachment}
                                        className="max-h-64 rounded-xl border border-white/60 object-contain"
                                      />
                                    )}
                                    {attachment.type === "location" && attachment.url && (
                                      <a
                                        href={attachment.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-2 rounded-xl bg-white/70 px-3 py-2 text-sm font-semibold text-emerald-800"
                                      >
                                        <MapPin className="h-4 w-4" />
                                        {copy.openLocation}
                                      </a>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      {canRecallMessage(message, currentRole) && onRecallMessage && (
                        <button
                          type="button"
                          onClick={() => onRecallMessage(message.id)}
                          className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-stone-500 hover:text-rose-700"
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                          {copy.recall}
                        </button>
                      )}
                    </div>
                  </article>
                  </Fragment>
                );
              })
            )}
            {(newMessagesAvailable || (driverMobileLayout && !isAtBottom)) && (
              <button
                type="button"
                onClick={scrollMessagesToBottom}
                className={`sticky bottom-2 mx-auto rounded-full bg-[#d8b769] px-4 py-2 text-xs font-bold text-black shadow-lg shadow-[#d8b769]/25 ${driverMobileLayout ? "trip-chat-mobile-latest-button" : ""}`}
              >
                <ChevronDown aria-hidden="true" />
                {newMessagesAvailable ? copy.newMessages : copy.backToLatest}
              </button>
            )}
          </div>
          <form onSubmit={handleSubmit} className={`border-t border-amber-100 bg-white p-4 ${driverMobileLayout ? "trip-chat-mobile-composer" : ""}`}>
            <div className={`flex flex-col gap-2 sm:flex-row ${driverMobileLayout ? "trip-chat-mobile-composer-row" : ""}`}>
              <textarea
                ref={messageInputRef}
                value={messageBody}
                onChange={(event) => setMessageBody(event.target.value)}
                className="min-h-[88px] flex-1 rounded-2xl border border-amber-200 bg-[#fffaf2] px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder={copy.typeMessage}
              />
              <div className={`flex gap-2 sm:self-end ${driverMobileLayout ? "trip-chat-mobile-composer-actions" : ""}`}>
                {driverMobileLayout && showTemplates && templates.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setServiceExceptionExpanded(false);
                      setQuickReplyExpanded((expanded) => !expanded);
                    }}
                    className={`trip-chat-mobile-quick-trigger ${quickReplyExpanded ? "is-active" : ""}`}
                    aria-label={copy.quickReply}
                    aria-expanded={quickReplyExpanded}
                    data-testid="trip-chat-quick-reply-trigger"
                  >
                    <span aria-hidden="true">⚡</span>
                  </button>
                )}
                <label aria-label={copy.attachImage} className="flex cursor-pointer items-center justify-center rounded-2xl border border-amber-200 bg-white px-3 py-3 text-stone-700 hover:bg-amber-50">
                  <Image className="h-4 w-4" />
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                </label>
                <button
                  type="button"
                  onClick={handleSendLocation}
                  aria-label={copy.sendLocation}
                  className="flex items-center justify-center rounded-2xl border border-amber-200 bg-white px-3 py-3 text-stone-700 hover:bg-amber-50"
                >
                  <MapPin className="h-4 w-4" />
                </button>
                <button
                  type="submit"
                  disabled={sending || !messageBody.trim()}
                  className="flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white disabled:opacity-50"
                >
                  <Send className="h-4 w-4" />
                  {sending ? copy.sending : copy.send}
                </button>
              </div>
            </div>
          </form>
        </section>
      </main>
    </div>
  );
}
